import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertTriangle, AlertCircle, Activity as ActivityIcon, ChevronRight, RefreshCw } from "lucide-react";
import { useOpsHealth } from "@/hooks/useOpsHealth";

const SEV_TINTS = {
    critical: "bg-rose-50  border-rose-200  text-rose-700",
    warn:     "bg-amber-50 border-amber-200 text-amber-700",
    info:     "bg-slate-50 border-slate-200 text-slate-600",
};

const SEV_ICONS = {
    critical: AlertCircle,
    warn:     AlertTriangle,
    info:     ActivityIcon,
};

const KIND_LABELS = {
    stagnant_deal:  "Stagnant deal",
    stale_client:   "Stale clinic",
    late_case:      "Late case",
    low_feedback:   "Low NPS",
    trial_expiring: "Trial ending",
    failed_payment: "Failed payment",
};

/**
 * Morning-coffee widget on the main Admin Dashboard. Aggregates 4-6 signals
 * from `/api/admin/ops-health` and renders them as a scrollable, click-through
 * feed grouped by severity. Each row deep-links to the underlying record.
 *
 * Keeps it small and embedded — does NOT take over the page like the older
 * platform-metrics dashboard does. The lab manager glances at it over coffee,
 * clicks one or two, moves on.
 */
export default function OpsHealthWidget() {
    const { data, counts, total, refresh, loading } = useOpsHealth();
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState("all");  // all | critical | warn | info

    const handleRefresh = async () => {
        setRefreshing(true);
        await refresh();
        setRefreshing(false);
    };

    const items = (data?.items || []).filter((i) => filter === "all" || i.severity === filter);

    return (
        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="ops-health-widget">
            <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100 gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <ActivityIcon size={15} className="text-slate-500 shrink-0" />
                    <div className="min-w-0">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">· Operational health</div>
                        <h3 className="font-display text-[15px] tracking-tight text-slate-900 truncate">
                            {loading ? "Loading…" : total === 0 ? "All clear ✓" : `${total} item${total === 1 ? "" : "s"} need a look`}
                        </h3>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <Chip active={filter === "all"}      label={`All ${total}`}                     tone="slate"   onClick={() => setFilter("all")}      testid="ops-filter-all" />
                    <Chip active={filter === "critical"} label={`Critical ${counts.critical || 0}`} tone="rose"    onClick={() => setFilter("critical")} testid="ops-filter-critical" />
                    <Chip active={filter === "warn"}     label={`Warn ${counts.warn || 0}`}         tone="amber"   onClick={() => setFilter("warn")}     testid="ops-filter-warn" />
                    <Chip active={filter === "info"}     label={`Info ${counts.info || 0}`}         tone="slate"   onClick={() => setFilter("info")}     testid="ops-filter-info" />
                    <button
                        onClick={handleRefresh}
                        data-testid="ops-refresh"
                        className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        aria-label="Refresh"
                    >
                        {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>
            ) : items.length === 0 ? (
                <div className="px-5 py-10 text-center">
                    <div className="text-[26px]">☕</div>
                    <div className="text-[13.5px] text-slate-600 mt-2">Nothing to action right now.</div>
                    <div className="text-[11.5px] text-slate-400 mt-1">Enjoy your coffee.</div>
                </div>
            ) : (
                <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                    {items.map((it, idx) => (
                        <OpsRow key={`${it.kind}-${it.ref_id}-${idx}`} item={it} />
                    ))}
                </ul>
            )}
        </section>
    );
}

function Chip({ active, label, tone, onClick, testid }) {
    const tones = {
        slate: active ? "bg-slate-900 text-white"  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50",
        rose:  active ? "bg-rose-600  text-white"  : "bg-white text-rose-700  border border-rose-200  hover:bg-rose-50",
        amber: active ? "bg-amber-500 text-white"  : "bg-white text-amber-700 border border-amber-200 hover:bg-amber-50",
    };
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${tones[tone] || tones.slate}`}
        >
            {label}
        </button>
    );
}

function OpsRow({ item }) {
    const Icon = SEV_ICONS[item.severity] || ActivityIcon;
    const when = fmtRelative(item.at);
    // Note: many `href`s are hash-routes (`/admin#sales`) that the parent admin
    // shell does NOT auto-route. We catch those clicks and emit a custom event
    // the Admin page listens for to switch tabs. Real page links (e.g.
    // `/cases/{id}`) fall through normally to React Router.
    const isHash = item.href && item.href.startsWith("/admin#");
    const handleClick = (e) => {
        if (isHash) {
            e.preventDefault();
            const tabId = item.href.split("#")[1];
            window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: { tabId } }));
        }
    };
    return (
        <li className="hover:bg-slate-50/60">
            <Link
                to={item.href || "#"}
                onClick={handleClick}
                data-testid={`ops-row-${item.kind}-${item.ref_id || ""}`}
                className="flex items-start gap-3 px-5 py-3"
            >
                <div className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-md border shrink-0 ${SEV_TINTS[item.severity] || SEV_TINTS.info}`}>
                    <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13.5px] font-medium text-slate-900 truncate">{item.title}</span>
                        <span className="font-mono-tr text-[10px] tracking-[0.12em] uppercase text-slate-400">{KIND_LABELS[item.kind] || item.kind}</span>
                    </div>
                    <div className="text-[12px] text-slate-500 truncate mt-0.5">{item.subtitle}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0 self-center text-[11px] text-slate-400">
                    <span>{when}</span>
                    <ChevronRight size={12} />
                </div>
            </Link>
        </li>
    );
}

function fmtRelative(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60)             return "now";
        if (diff < 3600)           return `${Math.floor(diff / 60)}m`;
        if (diff < 86400)          return `${Math.floor(diff / 3600)}h`;
        if (diff < 86400 * 7)      return `${Math.floor(diff / 86400)}d`;
        if (diff < 86400 * 365)    return `${Math.floor(diff / 86400 / 7)}w`;
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    } catch {
        return iso;
    }
}
